import { useState, useRef, useEffect } from "react";

export const useAudioRecorder = (mimeType, userId, enableTranscription = true) => {
  const [recording, setRecording] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(1);
  const [transcript, setTranscript] = useState("");
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);

  const volumeTimeout = useRef(null);

  const streamRef = useRef(null);
  const cancelRef = useRef(false);

  const processTextInput = async (inputText) => {
    setTranscript(inputText);
  };

  const monitorVolume = () => {
    volumeTimeout.current = setInterval(() => {
      if (analyserRef.current) {
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteTimeDomainData(dataArray);

        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const float = (dataArray[i] - 128) / 128;
          sum += Math.abs(float);
        }
        const averageVolume = sum / dataArray.length;
        setVolumeLevel(128 * Math.min(averageVolume * 2, 1.25));
      }
    }, 200);
  };

  const checkMicPermission = async () => {
    try {
      const permission = await navigator.permissions.query({ name: "microphone" });

      if (permission.state === "denied") {
        alert("Microphone access is blocked. Please enable it in browser settings.");
        return false;
      }

      return true;
    } catch {
      // Safari fallback (permissions API not supported)
      return true;
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const uploadAudio = async (audioBlob) => {
    setIsUploadingAudio(true);

    const formData = new FormData();
    formData.append("file", new File([audioBlob], "audio_file.mp4", { type: mimeType }));

    const response = await fetch(
      `${process.env.REACT_APP_MK_AGENT_API}/dictation/upload/${userId}`,
      {
        method: "POST",
        body: formData,
      }
    );

    const res = await response.json();

    if (response.ok) {
      processTextInput(res.data.content);
    } else {
      console.error("Error uploading file:", res);
      return;
    }
    setIsUploadingAudio(false);
  };

  const startRecording = async () => {
    try {

      const hasPermission = await checkMicPermission();
      if (!hasPermission) return;
      
      // reset for next recording
      cancelRef.current = false;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      analyserRef.current = audioContext.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunks.current = [];

      if (volumeTimeout.current) {
        clearInterval(volumeTimeout.current);
        volumeTimeout.current = null;
        setVolumeLevel(1);
      }

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: mimeType });

        if (cancelRef.current) {
          cancelRef.current = false; // reset for next recording
          return;
        }

        //downloadBlob(audioBlob, "audio_file.mp4");

        uploadAudio(audioBlob);
      };

      mediaRecorderRef.current.start(1000);
      setRecording(true);

      monitorVolume();

    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (volumeTimeout.current) {
      clearInterval(volumeTimeout.current);
      volumeTimeout.current = null;
      setVolumeLevel(1);
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const cancelRecording = () => {

    cancelRef.current = true;
    stopRecording();   // reuse existing cleanup logic
  };

  return {
    recording,
    transcript,
    isUploadingAudio,
    isProcessing,
    volumeLevel,
    startRecording,
    stopRecording,
    cancelRecording,
    processTextInput,
  };
};
